<template>
  <span
    ref="outerRef"
    v-clickOutside="close"
    type="image"
    :style="node.attrs.style"
    contenteditable="false"
    class="relative select-none cursor-pointer font-Inter"
    :class="{'w-200 h-100': !imageLoaded}"
    data-drag-handle
    draggable="true"
    @mousedown="hideBubbleMenu"
    @touchstart="hideBubbleMenu"
    @mouseup="removeGrabbingCursor"
    @dblclick.stop="openImageModal"
  >
    <LoadingModule
      :loading="!imageLoaded"
      class="absolute z-50 w-200 h-100"
    />

    <div class="relative mr-10" :class="node.attrs.class">
      <div
        v-if="active"
        class="absolute top-0 right-0 mt-2 mr-10 flex items-center z-20"
      >
        <!-- S H A D O W  I C O N -->
        <i
          v-if="isShadow && (user || share.mode === 'write')"
          class="icon-box-shadow cursor-pointer text-20 text-white opacity-90 hover:opacity-100 mr-10"
          @click="toggleImageBoxShadow(false)"
        />

        <!-- C O M M E N T  C O M P O N E N T -->
        <Comment
          v-if="node.attrs.imageComment"
          class="mr-10"
          :editor="editor"
          :node="node"
          :node-pos="nodePos"
        />

        <ImageMore
          v-if="editor.isEditable"
          contenteditable="false"
          :node="node"
          :editor="editor"
          :is-image-box-shadow="isImageBoxShadow"
          :is-link-menu-active="linkMenuIsActive"
          :is-comment-active="isCommentActive"
          :image-width="imageWidth"
          :node-pos="nodePos"
          @toggle-box-shadow="toggleImageBoxShadow"
          @toggle-link-menu="$event => linkMenuIsActive = $event"
          @toggle-comment-menu="$event => isCommentActive = $event"
        />
      </div>

      <!-- G O  T O  L I N K  B U T T O N -->
      <div v-if="active && node.attrs.imageLink" class="z-10 absolute left-1/2 transform -translate-x-1/2 bottom-30">
        <ButtonModule
          text="Go to link"
          class="bg-white rounded-4 text-13 px-35 py-10 shadow text-grey-main font-medium"
          prefix="icon-link text-14"
          has-hover
          button-opacity="90"
          @click.stop="openLink"
        />
      </div>

      <span
        v-if="active"
        contenteditable="false"
        class="resizer select-none z-10"
        @mousedown.prevent="onMouseDown"
        @touchstart.prevent.stop="touchStart"
        @touchend.prevent.stop="touchEnd"
        @touchmove.passive.stop="touchMove"
      >
        <span class="resizer__bar shadow-resizer select-none" />
      </span>

      <div
        class="absolute w-full h-full z-5 transition-bg duration-200 ease-in-out"
        :class="{'image-active': active}"
        @click="toggleActive"
      />

      <img
        v-show="imageLoaded"
        :id="`${node.attrs.id}`"
        ref="image"
        class="select-none"
        contenteditable="true"
        :src="node.attrs.src"
        style="width: 100%;"
        @load="imageOnLoad"
      >
    </div>

    <!-- I M A G E  M O D A L -->
    <ImageModal
      ref="imageModalRef"
      :node="node"
      :editor="editor"
      :node-pos="nodePos"
      :is-modal-menu-link-active="isModalMenuLinkActive"
      :is-modal-menu-comment-active="isModalMenuCommentActive"
      @toggle-modal-menu-link="$event => isModalMenuLinkActive = $event"
      @toggle-modal-menu-comment="$event => isModalMenuCommentActive = $event"
    />
  </span>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex'
import draggableMixin from '@/mixins/draggable'
import ImageModal from '@/components/image/ImageModal'
import ImageMore from '@/components/image/ImageMore'
import Comment from '@/components/image/Comment'
import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import { attachmentService } from '@/services'
import { Selection } from 'prosemirror-state'
import LoadingModule from '@/components/shared/LoadingModule'

export default {
  components: {
    ImageModal,
    ImageMore,
    Comment,
    ButtonModule,
    LoadingModule
  },

  mixins: [draggableMixin],

  props: {
    node: { type: Object },
    editor: { type: Object },
    getPos: { type: Function },
    updateAttributes: { type: Function }
  },

  data () {
    return {
      cursorTimeout: null,
      active: false,
      x: 0,
      width: 0,
      imageLoaded: false,
      timer: null,
      editorWidth: null,
      isImageBoxShadow: false,
      linkMenuIsActive: false,
      isCommentActive: false,
      isModalMenuLinkActive: false,
      isModalMenuCommentActive: false,
      imageWidth: 0
    }
  },

  /* COMPUTED */
  computed: {
    ...mapGetters(['isMobile', 'isDarkMode']),

    isShadow () {
      return this.node &&
      this.node.attrs &&
      this.node.attrs.class &&
      this.node.attrs.class.includes('shadow-smooth')
    },

    nodePos () {
      return this.getPos()
    },

    ...mapState({
      user: state => state.profile.user,
      share: state => state.project.share
    })
  },

  /* WATCHERS */
  watch: {
    isMobile () {
      this.setMinWidth()
    },

    active: {
      immediate: true,
      handler (isActive) {
        if (isActive && (
          !this.linkMenuIsActive ||
          !this.isCommentActive ||
          !this.isModalMenuLinkActive ||
          !this.isModalMenuCommentActive
        )) {
          document.addEventListener('keydown', this.initListeners)
        } else {
          document.removeEventListener('keydown', this.initListeners)
        }
      }
    },

    linkMenuIsActive (isActive) {
      this.keyDownListener(isActive)
    },

    isCommentActive (isActive) {
      this.keyDownListener(isActive)
    },

    isModalMenuLinkActive (isActive) {
      this.keyDownListener(isActive)
    },

    isModalMenuCommentActive (isActive) {
      this.keyDownListener(isActive)
    }
  },

  /* HOOKS */
  created () {
    document.addEventListener('dragleave', this.removeGrabbingCursor)
    document.addEventListener('drop', this.resizeImagesOnDrop)
  },

  mounted () {
    this.$nextTick(() => {
      this.editorWidth = document.getElementById('editor')?.getBoundingClientRect()?.width
    })
  },

  beforeUnmount () {
    document.removeEventListener('keydown', this.initListeners)
    document.removeEventListener('dragleave', this.removeGrabbingCursor)
    document.removeEventListener('drop', this.resizeImagesOnDrop)
  },

  /* METHODS */
  methods: {
    ...mapMutations(['toggleBookLoading', 'setNotification']),

    openImageModal () {
      // if (this.$route.name === 'TaskBoard' || this.$route.name === 'TaskBoardTeam') return
      this.$refs.imageModalRef.open()
    },

    addGrabCursor () {
      this.cursorTimeout = setTimeout(() => {
        this.$refs.outerRef.classList.add('grab')
      }, 100)
    },

    removeGrabbingCursor () {
      // this.$refs.outerRef.classList.remove('grab')
      this.onDragEnd()
      // if (this.cursorTimeout) {
      //   clearTimeout(this.cursorTimeout)
      // }
    },

    resizeImagesOnDrop () {
      this.onDragEnd()

      const { view } = this.editor

      if (!view.editable) {
        return
      }

      const $pos = this.editor.state.selection.$anchor
      const from = $pos.pos - $pos.parentOffset
      const images = []

      view.state.doc.nodesBetween(from, $pos.pos + 2, (node, pos) => {
        if (node.type.name === 'image') {
          images.push({
            node: node,
            pos: pos
          })
        }
      })

      if (images.length > 1) {
        const width = `${Math.floor(100 / images.length)}%`

        images.map(item => {
          this.updateAttributes({
            id: item.node.attrs.id,
            src: item.node.attrs.src,
            style: `width: ${width}; position: relative; display: inline-block;
                    line-height: 0; vertical-align: text-bottom; max-width: 98%; min-width: 100px`,
            imageComment: item.node.attrs.imageComment,
            imageLink: item.node.attrs.imageLink,
            class: (typeof item.node.attrs.class === 'string') && item.node.attrs.class?.includes('shadow-smooth') ? 'shadow-smooth' : '',
            isChatEditor: this.editor.contentComponent?.attrs?.id === 'editor_chat'
          }, item.pos)
        })
      }

      // this.removeGrabbingCursor()
    },

    keyDownListener (isActive) {
      if (isActive) {
        document.removeEventListener('keydown', this.initListeners)
      } else {
        document.addEventListener('keydown', this.initListeners)
      }
    },

    imageOnLoad () {
      this.imageLoaded = true
      this.convertWitdh()
      this.setMinWidth()
    },

    // touch
    touchStart (e) {
      this.onDragEnd()
      const touch = e.touches[0]
      this.setDefaultValues(touch)
    },

    touchMove (e) {
      this.removeGrabbingCursor()
      const touch = e.touches[0]
      this.resize(touch)
    },

    touchEnd () {
      const outer = this.$refs.outerRef
      this.save(outer.style.width)
      this.onDragEnd()
    },
    // mouse
    onMouseDown (e) {
      this.onDragEnd()
      this.setDefaultValues(e)

      const onMouseMove = (e) => {
        this.resize(e)
      }

      const onMouseUp = (e) => {
        e.preventDefault()
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)

        const outer = this.$refs.outerRef
        this.save(outer.style.width)
        this.onDragEnd()
      }

      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    },

    save (width) {
      this.updateAttributes({
        id: this.node.attrs.id,
        src: this.node.attrs.src,
        style: `width: ${width}; position: relative; display: inline-block;
                line-height: 0; vertical-align: text-bottom; max-width: 98%; min-width: 100px`,
        imageComment: this.node.attrs.imageComment,
        imageLink: this.node.attrs.imageLink,
        class: (typeof this.node.attrs.class === 'string') && this.node.attrs.class?.includes('shadow-smooth') ? 'shadow-smooth' : '',
        isChatEditor: this.editor.contentComponent?.attrs?.id === 'editor_chat'
      }, this.nodePos)
    },

    resize (e) {
      const outer = this.$refs.outerRef
      const diffInPx = parseInt(this.width) + (e.clientX - this.x)
      this.imageWidth = diffInPx

      if (diffInPx < 130) {
        outer.style.width = `${(130 / this.editorWidth * 100).toFixed()}%`
        return
      }

      if (diffInPx > this.editorWidth) {
        outer.style.width = '100%'
      } else {
        outer.style.width = `${(diffInPx / this.editorWidth * 100).toFixed()}%`
      }
    },

    setDefaultValues (e) {
      const outer = this.$refs.outerRef
      this.x = e.clientX
      this.editorWidth = document.getElementById('editor')?.getBoundingClientRect()?.width
      this.width = parseInt(outer.style.width) * this.editorWidth / 100
    },

    convertWitdh () {
      // convert width from px to percent
      // when uploading an image, width = 200 pixels
      const imageWidth = parseInt(this.$refs.outerRef?.style?.width)

      // if (this.editor.contentComponent?.attrs?.id === 'editor_chat') {
      //   this.save('100%')
      // } else {
      //   if (imageWidth > 100) {
      //     const width = `${(imageWidth / this.editorWidth * 100).toFixed()}%`
      //     this.save(width)
      //   }
      // }
      if (imageWidth > 100) {
        const width = `${(imageWidth / this.editorWidth * 100).toFixed()}%`
        this.save(width)
      }
    },

    setMinWidth () {
      if (this.$refs.outerRef) {
        const outerWidth = this.$refs.outerRef.getBoundingClientRect().width
        const editorWidth = document.getElementById('editor').getBoundingClientRect().width

        if (outerWidth <= 130) {
          const width = `${(130 / editorWidth * 100).toFixed()}%`
          this.save(width)
        }
      }
    },

    toggleActive () {
      if (!this.editor.isEditable) return
      // set cursor next to image
      const resolvePos = this.editor.view.state.doc.resolve(this.getPos())
      const selection = Selection.near(resolvePos)
      const tr = this.editor.view.state.tr.setSelection(selection)
      const state = this.editor.view.state.apply(tr)
      this.editor.view.updateState(state)
      this.editor.view.dispatch(this.editor.state.tr.scrollIntoView())

      this.$refs.outerRef.scrollIntoView({ block: 'center' })

      this.editor.chain().blur()
      this.active = !this.active
    },

    close () {
      this.active = false
    },

    hideBubbleMenu () {
      // this.addGrabCursor()

      const drag = () => {
        this.onStart()
        const el = document.getElementById('menububble')
        if (el.classList.contains('is-active')) el.classList.remove('is-active')
      }

      const drop = () => {
        this.removeGrabbingCursor()
        this.editor.chain().blur()

        document.removeEventListener('drag', drag)
        document.removeEventListener('drop', drop)
        // document.removeEventListener('dragstart', dragstart)
      }

      document.addEventListener('drop', drop)
      document.addEventListener('drag', drag)
      // document.addEventListener('dragstart', dragstart)
    },

    deleteImage (e) {
      const id = parseInt(this.node.attrs.id)

      if ((e.keyCode === 8 || e.keyCode === 46) && this.active && id && !this.linkMenuIsActive) {
        this.editor.commands.deleteImage(this.getPos())
      }
    },

    async setToClipboard (blob) {
      /* eslint-disable-next-line */
      const data = [new ClipboardItem({ [blob.type]: blob })]
      try {
        await navigator.clipboard.write(data)
        this.toggleBookLoading(false)
        this.setNotification({ isVisible: true, message: 'Image copied to clipboard', color: 'success' })
      } catch (error) {
        this.toggleBookLoading(false)
      }
    },

    convertToPngAndCopyToClipboard (blob) {
      const imageUrl = window.URL.createObjectURL(blob)
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      if (ctx) {
        const imageEl = document.createElement('img')
        imageEl.src = imageUrl
        imageEl.crossOrigin = 'anonymous'

        imageEl.onload = ({ target }) => {
          const { width, height } = target

          canvas.width = width
          canvas.height = height
          ctx.drawImage(target, 0, 0, width, height)
          canvas.toBlob(this.setToClipboard, 'image/png', 1)
        }
      }
    },

    imageToBlob (imageURL) {
      const img = new Image()
      const c = document.createElement('canvas')
      const ctx = c.getContext('2d')
      img.crossOrigin = ''
      img.src = imageURL
      return new Promise(resolve => {
        img.onload = function () {
          c.width = this.naturalWidth
          c.height = this.naturalHeight
          ctx.drawImage(this, 0, 0)
          c.toBlob((blob) => {
            // here the image is a blob
            resolve(blob)
          }, 'image/png', 0.75)
        }
      })
    },

    async copyImageToClipBoard (e) {
      const src = this.node.attrs.src
      const key = e.which || e.keyCode
      const ctrlKey = e.ctrlKey || e.metaKey

      if (key === 67 && ctrlKey) {
        this.toggleBookLoading(true)
        const isGiphy = (/giphy/i).test(this.node.attrs.src)

        if (isGiphy) {
          this.imageToBlob(this.node.attrs.src)
            .then(blob => this.convertToPngAndCopyToClipboard(blob))
            .finally(() => this.toggleBookLoading(false))
        } else {
          attachmentService.proxy(src)
            .then(res => {
              const blob = res.data

              if (src.endsWith('.jpg') || src.endsWith('.jpeg') || src.endsWith('.gif')) {
                this.convertToPngAndCopyToClipboard(blob)
              } else if (src.endsWith('.png')) {
                this.setToClipboard(blob)
              }
            })
            .finally(() => this.toggleBookLoading(false))
        }
      }
    },

    initListeners (e) {
      this.deleteImage(e)
      this.copyImageToClipBoard(e)
    },

    toggleImageBoxShadow (isBoxShadow) {
      this.editor.chain().blur()
      this.isImageBoxShadow = isBoxShadow
      this.editor.commands.setImageBoxShadow(this.isImageBoxShadow, this.getPos())
    },

    openLink () {
      window.open(this.node.attrs.imageLink)
    }
  }
}
</script>

<style lang="scss" scoped>
.resizer {
  top: calc(50% + 15px);
  transform: translateY(-50%);
  @apply absolute right-0 w-22 h-1/2;

  &__bar {
    cursor: col-resize;
    @apply ml-5 w-9 bg-primary block h-full rounded-4;
  }
}
.image-active {
  background: #3FA2FE;
  opacity: 0.4;
}

.image-width {
  max-width: calc(100% - 10px);
  @screen md {
    @apply w-full max-w-full;
  }
}
</style>
